/**
 * A minimal pinhole camera for 2D-canvas 3D scenes.
 *
 * Deliberately dependency-free and framework-free: any surface that can draw
 * to a canvas — or position a DOM node — can share one camera and get results
 * that agree with each other. That agreement is the whole point. When the
 * background and the foreground elements are projected by the same object,
 * the foreground is *in* the scene rather than layered on top of it.
 *
 * Coordinate system: +X right, +Y down, +Z away from the viewer.
 */

export interface Vec2 {
    x: number;
    y: number;
}

export interface Projected {
    /** Screen position in CSS pixels */
    x: number;
    y: number;
    /** Projection scale factor: focal / depth. Multiply world sizes by this. */
    k: number;
    /** Distance ahead of the camera, in world units */
    depth: number;
}

export interface CameraPose {
    /** Position along the flight axis */
    z: number;
    /** Barrel roll, radians */
    roll: number;
    /** Lateral/vertical drift of the camera itself */
    sway: Vec2;
}

export interface CameraOptions {
    /** Focal length in px. Larger = longer lens = less perspective distortion. */
    focal?: number;
    /** Nothing closer than this is drawn; it is behind or through the lens. */
    near?: number;
    /** Draw distance. Fog reaches 1 here. */
    far?: number;
    /** Vanishing point as a fraction of the viewport. */
    origin?: Vec2;
}

const DEFAULTS: Required<CameraOptions> = {
    focal: 900,
    near: 60,
    far: 5200,
    origin: { x: 0.5, y: 0.46 },
};

export class PinholeCamera {
    readonly focal: number;
    readonly near: number;
    readonly far: number;
    private origin: Vec2;

    private width = 0;
    private height = 0;
    private cx = 0;
    private cy = 0;

    private pose: CameraPose = { z: 0, roll: 0, sway: { x: 0, y: 0 } };
    private cosRoll = 1;
    private sinRoll = 0;

    constructor(opts: CameraOptions = {}) {
        const o = { ...DEFAULTS, ...opts };
        this.focal = o.focal;
        this.near = o.near;
        this.far = o.far;
        this.origin = o.origin;
    }

    setViewport(width: number, height: number): void {
        this.width = width;
        this.height = height;
        this.cx = width * this.origin.x;
        this.cy = height * this.origin.y;
    }

    setPose(pose: CameraPose): void {
        this.pose = pose;
        this.cosRoll = Math.cos(pose.roll);
        this.sinRoll = Math.sin(pose.roll);
    }

    get viewportWidth(): number { return this.width; }
    get viewportHeight(): number { return this.height; }
    /** Screen position of the vanishing point. */
    get vanishing(): Vec2 { return { x: this.cx, y: this.cy }; }

    /**
     * Project a world point to the screen.
     * Returns null when the point is behind the near plane.
     */
    project(x: number, y: number, z: number): Projected | null {
        const depth = z - this.pose.z;
        if (depth < this.near) return null;

        const dx = x - this.pose.sway.x;
        const dy = y - this.pose.sway.y;
        const rx = dx * this.cosRoll - dy * this.sinRoll;
        const ry = dx * this.sinRoll + dy * this.cosRoll;

        const k = this.focal / depth;
        return { x: this.cx + rx * k, y: this.cy + ry * k, k, depth };
    }

    /**
     * Atmospheric attenuation for a depth: 1 at the lens, 0 at `far`.
     * Squared, because linear fog reads as a flat wash rather than distance.
     */
    fog(depth: number): number {
        const t = 1 - depth / this.far;
        return t <= 0 ? 0 : t * t;
    }
}

/**
 * Small deterministic PRNG (Mulberry32). Seeded so a generated field is
 * identical on every load and every resize — a scene that reshuffles itself
 * when the window changes size reads as a bug, not as life.
 */
export function mulberry32(seed: number): () => number {
    let s = seed;
    return () => {
        s |= 0;
        s = (s + 0x6d2b79f5) | 0;
        let t = Math.imul(s ^ (s >>> 15), 1 | s);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

export type Rgb = [number, number, number];

export function hexToRgb(hex: string): Rgb {
    const h = hex.replace('#', '');
    return [
        parseInt(h.slice(0, 2), 16),
        parseInt(h.slice(2, 4), 16),
        parseInt(h.slice(4, 6), 16),
    ];
}

export function mixRgb(a: Rgb, b: Rgb, t: number): Rgb {
    return [
        a[0] + (b[0] - a[0]) * t,
        a[1] + (b[1] - a[1]) * t,
        a[2] + (b[2] - a[2]) * t,
    ];
}

export function rgba(c: Rgb, alpha: number): string {
    return `rgba(${c[0] | 0},${c[1] | 0},${c[2] | 0},${alpha.toFixed(3)})`;
}

export const clamp = (v: number, lo: number, hi: number): number =>
    v < lo ? lo : v > hi ? hi : v;

/**
 * Critically-damped follow. Frame-rate independent, so the camera has the same
 * weight on a 60Hz laptop and a 120Hz display.
 */
export function damp(current: number, target: number, lambda: number, dt: number): number {
    return target + (current - target) * Math.exp(-lambda * dt);
}
